import { AxiosResponse } from 'axios'
import { IFilter, Where } from 'types/common'
import { AggregationPipeline } from 'types/common/aggregation'
import { api } from 'API'
import { IDuplicateGroupDTO, IUpdateGroupMember } from 'components/Pages/UserPage/types'
import { AggregationPaginated } from 'interfaces/common'
import { IAggregateGroupMemberResult, IGroup, IGroupMember, IGroupMemberPermission } from 'interfaces/group'

export interface IOrgMemberPickerRow {
  membershipId: number
  userId: number
  firstName: string
  lastName: string
  username: string
  email: string
  fullName: string
}

export interface IOrgMemberPickerResult {
  items: IOrgMemberPickerRow[]
  totalCount: number
}

// Cross-org list calls send `orgIds` so the api client doesn't auto-stamp a
// single active-org `orgId` — the server validates this list and enforces it
// as the authoritative org scope for the aggregation.
const orgScopeConfig = (orgIds?: number[]) => (orgIds && orgIds.length ? { params: { orgIds } } : undefined)

export async function getGroupsByAggregate<T = IGroup>(
  pipeline: AggregationPipeline,
  orgIds?: number[]
): Promise<T[]> {
  try {
    const response: AxiosResponse = await api.post(`/groups/aggregate`, { pipeline }, orgScopeConfig(orgIds))
    return response?.data
  } catch (error: any) {
    return []
  }
}

export async function aggregateCountGroups(pipeline: AggregationPipeline, orgIds?: number[]): Promise<number> {
  try {
    const response: AxiosResponse = await api.post(`/groups/aggregate/count`, { pipeline }, orgScopeConfig(orgIds))
    return response?.data[0]?.totalResults ?? 0
  } catch (error: any) {
    return 0
  }
}

export async function aggregateGroupSearch(
  pipeline: AggregationPipeline,
  orgIds?: number[]
): Promise<AggregationPaginated<IGroup>> {
  try {
    const response: AxiosResponse = await api.post(`/groups/aggregate/search`, { pipeline }, orgScopeConfig(orgIds))
    return response?.data
  } catch (error: any) {
    return { paginatedResults: [], totalResults: 0 }
  }
}

export async function duplicateGroup(sourceGroupId: number, duplicateGroupDto: IDuplicateGroupDTO): Promise<IGroup> {
  const response: AxiosResponse = await api.post(`/groups/${sourceGroupId}/duplicate`, duplicateGroupDto)
  return response?.data
}

export async function getGroups(filter: IFilter<IGroup> = {}): Promise<IGroup[]> {
  const response: AxiosResponse = await api.get('/groups', { params: { filter } })
  return response.data
}

export async function getGroupById(id: number): Promise<IGroup> {
  const response: AxiosResponse = await api.get(`/groups/${id}`)
  return response.data
}

export async function countGroups(where: Where<IGroup> = {}): Promise<number> {
  const response: AxiosResponse = await api.get('/groups/count', { params: { where } })
  return +(response.data.count ?? 0)
}

export async function deleteGroupById(id: number): Promise<void> {
  await api.delete(`/groups/${id}`)
}

export async function createGroup(group: IGroup, orgId?: number): Promise<IGroup> {
  // Scope the request to the chosen team. The api client only auto-stamps orgId
  // when absent, so passing it here makes the server resolve + authorize that org.
  const response: AxiosResponse = await api.post(`/groups`, group, orgId ? { params: { orgId: orgId } } : undefined)
  return response.data
}

export async function updateGroupById(id: number, updatedGroup: IGroup): Promise<void> {
  await api.patch(`/groups/${id}`, updatedGroup)
}

export async function addGroupMembers(groupMembers: IGroupMember[], orgId?: number): Promise<IGroupMember[]> {
  const response: AxiosResponse = await api.post(
    '/group-members/batch-update',
    { toCreateGroupMembers: groupMembers },
    orgId ? { params: { orgId: orgId } } : undefined
  )
  return response.data
}

export async function updateGroupMemberById(id: number, updatedGroupMember: Partial<IGroupMember>): Promise<void> {
  await api.patch(`/group-members/${id}`, updatedGroupMember)
}

export async function removeGroupMemberById(id: number): Promise<void> {
  if (!id) {
    return
  }
  await api.delete(`/group-members/${id}`)
}

export async function updateGroupMembers(data: IUpdateGroupMember): Promise<void> {
  const response: AxiosResponse = await api.post('/group-members/batch-update', data)
  return response.data
}

export async function getGroupMemberPermissions(
  filter: IFilter<IGroupMemberPermission> = {}
): Promise<IGroupMemberPermission[]> {
  const response: AxiosResponse = await api.get('/group-member-permissions', { params: { filter } })
  return response.data
}

export async function getGroupMembers(
  id: number,
  filter: IFilter<IGroup> = {},
  orgId?: number
): Promise<IAggregateGroupMemberResult> {
  // Scope to the group's own team, not the active team (group can belong to any team).
  const response: AxiosResponse = await api.get(`/groups/${id}/aggregate-group-member`, {
    params: orgId ? { filter, orgId: orgId } : { filter }
  })
  return response.data
}

export async function getNumberOfGroupMember(id: number): Promise<number> {
  const response: AxiosResponse = await api.get(`/groups/${id}/count-member`)
  return response.data.count
}

export async function getGroupMembersFilter(filter: IFilter<IGroupMember> = {}): Promise<IGroupMember[]> {
  const response: AxiosResponse = await api.get(`/group-members?filter=${JSON.stringify(filter)}`)
  return response.data
}

export async function getOrgMembersForPicker(
  search: string,
  limit: number,
  offset: number,
  orgId?: number
): Promise<IOrgMemberPickerResult> {
  try {
    // Pick from the group's own team so the candidates are addable to the group.
    const response: AxiosResponse = await api.get('/groups/org-members-picker', {
      params: orgId ? { search, limit, offset, orgId: orgId } : { search, limit, offset }
    })
    return response.data
  } catch {
    return { items: [], totalCount: 0 }
  }
}
